import { formatCurrency } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { TariffSummaryResults } from '../../../../assets/js/com/ts_api_client';
import { CUSTOMERMODE } from '../../enums/customerMode';
import { TariffPriceAnnual } from '../../../shared/models/tariff-price.annual';
import { getInfoIText, KEY_FOR_CHARGE_FLEX_BONUS, TariffAdvisorService } from '../../services/tariff-advisor.service';
import { TARIFF_TEXT_KEYS } from '../../enums/tariffTextKeys';
import { TranslateService } from '@ngx-translate/core';

const MAX_BENEFITS: number = 4;
type IconType = 'checkmark' | 'gift';

interface Benefit {
  icon: IconType,
  text: string
}

@Component({
    selector: 'bdo-tariff-tile',
    templateUrl: './tariff-tile.component.html',
    styleUrls: ['./tariff-tile.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TariffTileComponent implements OnChanges {
  @Input() tariffData: TariffSummaryResults;
  @Input() isSmall: boolean = false;
  @Output() selectTariff = new EventEmitter<TariffSummaryResults>();

  public iconSrc: string;
  public showTariffDetails: boolean = false;
  public isGewe: boolean;
  public hasBonus: boolean;
  public prices: TariffPriceAnnual[];
  public infotext: string = '';
  public benefitsToShow: Benefit[] = [];

  constructor(
    public tariffAdvisorService: TariffAdvisorService,
    private translateService: TranslateService
  ) {}

  /**
   * find the charge flex bonus data in the tariffData, because there is a specific part
   * where only the charge flex bonus is displayed on the tile
   */
  getChargeFlexBonusValue(): number | null {
    const bonusPayment = this.tariffData?.parts
      ?.flatMap(part => part.contractPayments ?? [])
      ?.find(payment => payment.description?.includes(KEY_FOR_CHARGE_FLEX_BONUS));

    return bonusPayment ? Math.abs(bonusPayment.baseGross) : null;
  }

  updatePrices() {
    this.iconSrc = this.tariffAdvisorService.getIconSrc(this.tariffData?.iconUrl, this.tariffData?.division);
    this.isGewe = this.tariffData?.customerType === CUSTOMERMODE.GEWE;
    this.hasBonus = this.tariffData?.parts[0]?.contractPayments.some(elem => elem.description.length > 0);
    this.prices = this.tariffAdvisorService.getPeriodicPricesByTimeSlices(this.tariffData);
    const isDynamic = this.tariffData?.dynamictariff;

    const infoKey = isDynamic
      ? TARIFF_TEXT_KEYS.InfoI_withDynamic
      : (this.hasBonus ? TARIFF_TEXT_KEYS.InfoI_withBonus : TARIFF_TEXT_KEYS.InfoI_withoutBonus);
    this.infotext = getInfoIText(this.tariffData?.infois, infoKey);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updatePrices();
    
    if (this.tariffData?.parts[0]?.contractPayments || this.tariffData?.staticBenefits) {
      this.getBenefits();
    }
  }

  getBenefits() {
    this.benefitsToShow = [];
    const contractPayments = this.tariffData?.parts?.[0]?.contractPayments || [];
    const staticBenefits = this.tariffData?.staticBenefits || [];
    const countBenefitsCMS = MAX_BENEFITS - contractPayments.length;

    for (let currentBenefit = 0; currentBenefit < countBenefitsCMS && currentBenefit < staticBenefits.length; currentBenefit++) {
      const benefit = staticBenefits[currentBenefit];
      if (benefit) {
        this.benefitsToShow.push({
          icon: benefit.icon as IconType,
          text: benefit.text
        });
      }
    }

    for (const bonus of contractPayments) {
      const price = Math.abs(this.isGewe ? bonus.baseNet : bonus.baseGross);
      this.benefitsToShow.push({
        icon: 'gift',
        text: formatCurrency(price, 'de-DE', '€', 'EUR') + ' ' + bonus.description
      });
    }
  }

  /**
   * close tariff-details modal
   */
  closeTariffDetails() {
    this.showTariffDetails = false;
  }

  onSelect() {
    this.selectTariff.emit(this.tariffData);
  }


  /**
   * toggle optionstate and emit changed optionId, enable-state and frameContractId
   * @param optionId

   toggleOption(optionId: string) {
    const optionOfTariff = this.tariff?.options.find((item) => item.data.optionId === optionId);
    if (optionOfTariff) {
      optionOfTariff.enabled = !optionOfTariff.enabled;
    }
    this.changeOption.emit({ optionId: optionOfTariff.data.optionId, enabled: optionOfTariff.enabled, frameContractId: this.tariff.frameContractId });
  }
  */
}
